import {useEffect, useState} from 'react'
import {Card, Nav} from 'react-bootstrap'
import {toast} from 'react-toastify'

import {useMutation} from '@apollo/client'
import {UPDATE_AI_CONFIG, UPDATE_PROMPT_CONFIG} from '../../../gql/mutations/channelMutations'
import {useAuth} from '../../../../modules/auth'
import {parseId} from '../../../helpers'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {useGetUser} from '../../../store/userStore'
import {IAllUserNode} from '../../../types/users'
import {
  ChannelConfigData,
  TelephonyProviderValue,
  VicidialCampaignOption,
  fetchVicidialCampaignOptions,
  useChannelConfig,
  useUserChannelConfig,
} from '../../channelConfig/hooks/useChannelConfig'
import {PersonalSettingsTab} from './PersonalSettingsTab'
import {OrganizationChannelsTab} from './OrganizationChannelsTab'
import {FormattedMessage, useIntl} from 'react-intl'
import {ONE_USER_MESSAGES as M} from '../oneUserMessages'

interface Props {
  node: IAllUserNode
}

type UserTelephonyForm = Pick<
  ChannelConfigData,
  | 'telephony_provider'
  | 'vicidial_enabled'
  | 'vicidial_agent_user'
  | 'vicidial_agent_password'
  | 'vicidial_phone_login'
  | 'vicidial_phone_password'
  | 'vicidial_campaign_id'
  | 'vicidial_dial_queue_delay_seconds'
  | 'vicidial_supervisor_access'
  | 'twilio_provisioning_enabled'
  | 'twilio_provisioning_max_numbers'
  | 'email_enabled'
  | 'email_host'
  | 'email_port'
  | 'email_host_user'
  | 'email_host_password'
  | 'email_use_tls'
  | 'email_from'
  | 'email_reply_to'
  | 'healthsherpa_enabled'
  | 'healthsherpa_email'
  | 'healthsherpa_api_key'
  | 'healthsherpa_webhook_secret'
  | 'dify_enabled'
  | 'dify_api_key'
  | 'dify_api_url'
  | 'dify_app_id'
  | 'ai_provider'
  | 'llm_provider'
  | 'llm_api_key'
  | 'llm_model'
  | 'prompt_header'
  | 'prompt_body'
  | 'prompt_footer'
  | 'share_contact_info'
  | 'share_quotes'
  | 'share_policies'
  | 'share_leads'
  | 'share_consent_status'
>

const EMPTY_USER_TELEPHONY_FORM: UserTelephonyForm = {
  telephony_provider: '',
  vicidial_enabled: false,
  vicidial_agent_user: '',
  vicidial_agent_password: '',
  vicidial_phone_login: '',
  vicidial_phone_password: '',
  vicidial_campaign_id: '',
  vicidial_dial_queue_delay_seconds: null,
  vicidial_supervisor_access: null,
  twilio_provisioning_enabled: false,
  twilio_provisioning_max_numbers: 2,
  email_enabled: false,
  email_host: '',
  email_port: 587,
  email_host_user: '',
  email_host_password: '',
  email_use_tls: true,
  email_from: '',
  email_reply_to: '',
  healthsherpa_enabled: false,
  healthsherpa_email: '',
  healthsherpa_api_key: '',
  healthsherpa_webhook_secret: '',
  dify_enabled: false,
  dify_api_key: '',
  dify_api_url: 'https://api.dify.ai/v1',
  dify_app_id: '',
  ai_provider: 'DIFY',
  llm_provider: 'ANTHROPIC',
  llm_api_key: '',
  llm_model: '',
  prompt_header: '',
  prompt_body: '',
  prompt_footer: '',
  share_contact_info: null,
  share_quotes: null,
  share_policies: null,
  share_leads: null,
  share_consent_status: null,
}

const EMPTY_USER_POLICYDIAL_FORM: Pick<
  UserTelephonyForm,
  | 'telephony_provider'
  | 'vicidial_enabled'
  | 'vicidial_agent_user'
  | 'vicidial_agent_password'
  | 'vicidial_phone_login'
  | 'vicidial_phone_password'
  | 'vicidial_campaign_id'
  | 'vicidial_dial_queue_delay_seconds'
  | 'vicidial_supervisor_access'
> = {
  telephony_provider: '',
  vicidial_enabled: false,
  vicidial_agent_user: '',
  vicidial_agent_password: '',
  vicidial_phone_login: '',
  vicidial_phone_password: '',
  vicidial_campaign_id: '',
  vicidial_dial_queue_delay_seconds: null,
  vicidial_supervisor_access: null,
}

const buildUserTelephonyForm = (config?: Partial<ChannelConfigData>): UserTelephonyForm => ({
  telephony_provider: (config?.telephony_provider as TelephonyProviderValue) || '',
  vicidial_enabled: Boolean(config?.vicidial_enabled),
  vicidial_agent_user: config?.vicidial_agent_user || '',
  vicidial_agent_password: config?.vicidial_agent_password || '',
  vicidial_phone_login: config?.vicidial_phone_login || '',
  vicidial_phone_password: config?.vicidial_phone_password || '',
  vicidial_campaign_id: config?.vicidial_campaign_id || '',
  vicidial_dial_queue_delay_seconds:
    config?.vicidial_dial_queue_delay_seconds === null ||
    config?.vicidial_dial_queue_delay_seconds === undefined
      ? null
      : Number(config.vicidial_dial_queue_delay_seconds),
  vicidial_supervisor_access:
    config?.vicidial_supervisor_access === null || config?.vicidial_supervisor_access === undefined
      ? null
      : Boolean(config.vicidial_supervisor_access),
  twilio_provisioning_enabled: Boolean(config?.twilio_provisioning_enabled),
  twilio_provisioning_max_numbers: config?.twilio_provisioning_max_numbers ?? 2,
  email_enabled: Boolean(config?.email_enabled),
  email_host: config?.email_host || '',
  email_port: config?.email_port || 587,
  email_host_user: config?.email_host_user || '',
  email_host_password: config?.email_host_password || '',
  email_use_tls: config?.email_use_tls !== undefined ? Boolean(config.email_use_tls) : true,
  email_from: config?.email_from || '',
  email_reply_to: config?.email_reply_to || '',
  healthsherpa_enabled: Boolean(config?.healthsherpa_enabled),
  healthsherpa_email: config?.healthsherpa_email || '',
  healthsherpa_api_key: config?.healthsherpa_api_key || '',
  healthsherpa_webhook_secret: config?.healthsherpa_webhook_secret || '',
  dify_enabled: Boolean(config?.dify_enabled),
  dify_api_key: config?.dify_api_key || '',
  dify_api_url: config?.dify_api_url || 'https://api.dify.ai/v1',
  dify_app_id: config?.dify_app_id || '',
  ai_provider: (config?.ai_provider as any) || 'DIFY',
  llm_provider: (config?.llm_provider as any) || 'ANTHROPIC',
  llm_api_key: config?.llm_api_key || '',
  llm_model: config?.llm_model || '',
  prompt_header: config?.prompt_header || '',
  prompt_body: config?.prompt_body || '',
  prompt_footer: config?.prompt_footer || '',
  share_contact_info: config?.share_contact_info ?? null,
  share_quotes: config?.share_quotes ?? null,
  share_policies: config?.share_policies ?? null,
  share_leads: config?.share_leads ?? null,
  share_consent_status: config?.share_consent_status ?? null,
})

export const UserChannelConfigTab: React.FC<Props> = ({node}) => {
  const intl = useIntl()
  const {auth} = useAuth()
  const orgId = node.organization?.id ? `${parseId(node.organization.id)}` : undefined
  const userId = node.id ? `${parseId(node.id)}` : undefined
  const [updateAiConfigGql] = useMutation(UPDATE_AI_CONFIG)
  const [updatePromptConfigGql] = useMutation(UPDATE_PROMPT_CONFIG)

  const {config: orgConfig, loading: loadingOrgConfig} = useChannelConfig(orgId)
  const {
    config: userConfig,
    loading: loadingUserConfig,
    saving: savingUserConfig,
    saveUserConfig,
  } = useUserChannelConfig(userId)

  const [activeTab, setActiveTab] = useState<'personal' | 'organization'>('personal')
  const [userForm, setUserForm] = useState<UserTelephonyForm>(EMPTY_USER_TELEPHONY_FORM)
  const [campaignOptions, setCampaignOptions] = useState<VicidialCampaignOption[]>([])
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)
  const [isUserDirty, setIsUserDirty] = useState(false)
  const [showWebhookSecret, setShowWebhookSecret] = useState(false)

  const currentUserId = useGetUser((s) => s.user?.user?.id)
  const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const isAdmin = userGroups.includes('Administrador') || isSuperAdmin
  const isSupervisor = userGroups.includes('Supervisor')

  const isViewingOwnProfile = currentUserId && parseId(currentUserId) === parseId(node.id)
  const isSupervisorOfTarget =
    isSupervisor &&
    node.groupAssignmentSet?.edges?.some((edge) =>
      (edge.node?.supervisors?.edges || []).some(
        (s) => parseId(s.node.id) === parseId(currentUserId || '')
      )
    )
  const canAccess = isAdmin || (isSupervisor && isViewingOwnProfile) || isSupervisorOfTarget

  const targetUserGroupNames =
    node.groupAssignmentSet?.edges?.map((edge) => edge.node?.name).filter(Boolean) || []
  const targetUserHasSupervisorDefault = targetUserGroupNames.some((name) =>
    ['Administrador', 'SuperAdministrador', 'Supervisor'].includes(name as string)
  )

  const getToken = () => auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)

  useEffect(() => {
    if (!loadingUserConfig) {
      setUserForm(buildUserTelephonyForm(userConfig))
      setIsUserDirty(false)
    }
  }, [userConfig, loadingUserConfig])

  useEffect(() => {
    let cancelled = false

    const loadCampaigns = async () => {
      const token = getToken()
      if (
        !token ||
        !orgConfig.vicidial_base_url ||
        !orgConfig.vicidial_api_user ||
        !orgConfig.vicidial_api_password
      ) {
        setCampaignOptions([])
        return
      }

      setLoadingCampaigns(true)
      try {
        const campaigns = await fetchVicidialCampaignOptions({
          token,
          baseUrl: orgConfig.vicidial_base_url,
          apiUser: orgConfig.vicidial_api_user,
          apiPassword: orgConfig.vicidial_api_password,
        })
        if (!cancelled) {
          setCampaignOptions(campaigns)
        }
      } catch {
        if (!cancelled) {
          setCampaignOptions([])
        }
      } finally {
        if (!cancelled) {
          setLoadingCampaigns(false)
        }
      }
    }

    loadCampaigns()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgConfig.vicidial_base_url, orgConfig.vicidial_api_user, orgConfig.vicidial_api_password])

  const handleUserChange = (
    field: keyof UserTelephonyForm,
    value: string | boolean | number | null
  ) => {
    setUserForm((prev) => ({...prev, [field]: value}))
    setIsUserDirty(true)
  }

  const handleSaveUserConfig = async () => {
    try {
      await updateAiConfigGql({
        variables: {
          scope: 'user',
          targetId: userId ? parseInt(userId, 10) : undefined,
          difyEnabled: Boolean(userForm.dify_enabled),
          difyApiKey: userForm.dify_api_key || '',
          difyApiUrl: userForm.dify_api_url || 'https://api.dify.ai/v1',
          difyAppId: userForm.dify_app_id || '',
          aiProvider: userForm.ai_provider || 'DIFY',
          llmProvider: userForm.llm_provider || 'ANTHROPIC',
          llmApiKey: userForm.llm_api_key || '',
          llmModel: userForm.llm_model || '',
        },
      })
    } catch (gqlErr) {
      console.error('Error saving user Dify config via GraphQL:', gqlErr)
    }

    try {
      await updatePromptConfigGql({
        variables: {
          scope: 'user',
          targetId: userId ? parseInt(userId, 10) : undefined,
          promptBody: userForm.prompt_body || '',
          shareContactInfo: userForm.share_contact_info,
          shareQuotes: userForm.share_quotes,
          sharePolicies: userForm.share_policies,
          shareLeads: userForm.share_leads,
          shareConsentStatus: userForm.share_consent_status,
        },
      })
    } catch (gqlErr) {
      console.error('Error saving user Prompt config via GraphQL:', gqlErr)
    }

    const result = await saveUserConfig(userForm)
    if (result.ok) {
      toast.success(intl.formatMessage(M.ucToastSaved))
      setIsUserDirty(false)
      return
    }
    toast.error(intl.formatMessage(M.ucToastSaveError, {message: result.error}), {autoClose: 8000})
  }

  const handleResetToInherited = () => {
    setUserForm((prev) => ({...prev, ...EMPTY_USER_POLICYDIAL_FORM}))
    setIsUserDirty(true)
  }

  if (!canAccess) {
    return (
      <Card className='mb-5'>
        <Card.Body className='text-center py-10'>
          <i className='bi bi-lock-fill fs-1 text-muted mb-3 d-block' />
          <h5 className='text-gray-700'>
            <FormattedMessage {...M.ucAccessRestricted} />
          </h5>
          <p className='text-muted mb-0'>
            <FormattedMessage {...M.ucAccessRestrictedBody} />
          </p>
        </Card.Body>
      </Card>
    )
  }

  return (
    <>
      <Card className='mb-0'>
        <Card.Header className='border-0 pt-5 px-5 pb-0'>
          <Nav
            variant='tabs'
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k as 'personal' | 'organization')}
          >
            <Nav.Item>
              <Nav.Link eventKey='personal' className='fw-bold'>
                <i className='bi bi-person-gear me-2' />
                <FormattedMessage {...M.ucTabPersonal} />
                {node.firstName && (
                  <span className='text-muted ms-1'>
                    {intl.formatMessage(M.ucTabSuffix, {
                      name: `${node.firstName} ${node.lastName}`,
                    })}
                  </span>
                )}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='organization' className='fw-bold'>
                <i className='bi bi-broadcast me-2' />
                <FormattedMessage {...M.ucTabOrganization} />
                {node.organization?.name && (
                  <span className='text-muted ms-1'>
                    {intl.formatMessage(M.ucTabSuffix, {name: node.organization.name})}
                  </span>
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Header>

        <Card.Body className='p-5'>
          {activeTab === 'personal' && (
            <PersonalSettingsTab
              node={node}
              userForm={userForm}
              handleUserChange={handleUserChange as (field: string, value: any) => void}
              campaignOptions={campaignOptions}
              savingUserConfig={savingUserConfig}
              isUserDirty={isUserDirty}
              userId={userId}
              loadingUserConfig={loadingUserConfig}
              handleSaveUserConfig={handleSaveUserConfig}
              orgConfig={orgConfig}
              userConfig={userConfig}
              showWebhookSecret={showWebhookSecret}
              setShowWebhookSecret={setShowWebhookSecret}
              handleResetToInherited={handleResetToInherited}
            />
          )}

          {activeTab === 'organization' && (
            <OrganizationChannelsTab
              node={node}
              orgConfig={orgConfig}
              loadingOrgConfig={loadingOrgConfig}
              orgId={orgId}
            />
          )}
        </Card.Body>
      </Card>
    </>
  )
}
